/**
 * Templates API — system-wide template library (Milestone 1.5b, spec §3.12).
 *
 * Read open to any auth user; create/update/delete require system_role=founder.
 * Apply enforces target-scope ACL inline (org founder/admin for org_*).
 *
 * Mirrors apps/chaos-gateway/src/templates.ts.
 */
import type {
  ApplyTemplateInput,
  ApplyTemplateResult,
  CreateTemplateInput,
  Template,
  TemplateCategory,
  UpdateTemplateInput,
} from '@chaos/types/registries';

import { apiJson } from './client';
import { normalizeOrgPublications } from './org-publication-shape';

interface TemplatesListResponse {
  templates: Template[];
}
interface TemplateResponse {
  template: Template;
}

// ── Cross-org publication matrix (0079) ────────────────────────────────
// MIRRORS skillsApi (0078) EXACTLY. A template is PUBLISHED (made available)
// into an org via a template_org_publications row. // template is available in EVERY org of the account and the per-org cells render
// as inherited. `orgIds` is the explicit per-org set. FOUNDER-RESOLVED
// semantics: pure checkbox — All-orgs → everywhere; specific org rows → those;
// no rows → available nowhere. Publication is the availability gate; the
// per-target apply ACL is UNTOUCHED.
/**
 * WHERE an item is published, and which of those marks the "All organizations"
 * select-all button put there (migration 0168).
 *
 *   orgIds       — every organization the item is published into. THE list.
 *   viaAllOrgIds — the subset select-all placed (⊆ orgIds). The origin is
 *                  remembered because two rules depend on it: a new solo
 *                  Workspace inherits these marks, and the first other person
 *                  added to a Workspace switches them off. Marks placed by hand
 *                  are never touched by either.
 *
 * There is no `allOrgs` boolean any more. "Available everywhere" used to be a
 * separate STATUS row (org_id IS NULL) which made it impossible to exclude a
 * single organization — and which, because no read gate carried an account
 * filter, actually meant "every organization that will ever exist, on any
 * account". It is now simply the state where every organization is ticked,
 * which the UI reads off `orgIds` and therefore cannot disagree about.
 */
export interface TemplatePublications {
  orgIds: string[];
  viaAllOrgIds: string[];
}

// One column of the cross-org matrix — an org of the caller's account.
export interface TemplateMatrixOrg {
  id: string;
  displayName: string;
  logoUrl: string | null;
}

// One row of the matrix (GET /api/templates/all): the catalog Template plus its
// publication set. Templates have NO owner org (global catalog).
export interface TemplateMatrixRow extends Template {
  publications: TemplatePublications;
}

export interface TemplateMatrix {
  templates: TemplateMatrixRow[];
  orgs: TemplateMatrixOrg[];
}

interface TemplatePublicationsResponse {
  publications: TemplatePublications;
}

interface ListTemplatesParams {
  category?: TemplateCategory | string;
  [k: string]: string | number | boolean | undefined;
}

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export type ApplyTemplateResponse = ApplyTemplateResult & {
  template_slug: string;
  category_mismatch?: boolean;
};

export const templatesApi = {
  list(params: ListTemplatesParams = {}): Promise<Template[]> {
    return apiJson<TemplatesListResponse>(`/templates${qs(params)}`).then((r) => r.templates);
  },
  // 0079: account-wide matrix — every template in the global catalog, each with
  // its cross-org publication set, plus the account's orgs as the matrix columns.
  // Account-owner only (403 otherwise). Mirrors skillsApi.listAll().
  //
  // THE PUBLICATION SETS ARE NORMALISED AT THE DOOR (see
  // ./org-publication-shape) — the same guarantee <CrossOrgMatrix> already gets
  // from the connectors, permissions and roles readers it shares. Take it out
  // and a payload whose `orgIds` is not an array takes the section down through
  // `cellState`, which React evaluates while building the element tree.
  listAll(): Promise<TemplateMatrix> {
    return apiJson<TemplateMatrix>(`/templates/all`).then((m) => ({
      ...m,
      templates: m.templates.map((t) => ({
        ...t,
        publications: normalizeOrgPublications(t.publications),
      })),
    }));
  },
  get(templateId: string): Promise<Template> {
    return apiJson<TemplateResponse>(`/templates/${templateId}`).then((r) => r.template);
  },
  create(input: CreateTemplateInput): Promise<Template> {
    return apiJson<TemplateResponse>('/templates', { method: 'POST', json: input }).then(
      (r) => r.template,
    );
  },
  update(templateId: string, input: UpdateTemplateInput): Promise<Template> {
    return apiJson<TemplateResponse>(`/templates/${templateId}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.template);
  },
  delete(templateId: string): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(`/templates/${templateId}`, { method: 'DELETE' });
  },
  apply(templateId: string, input: ApplyTemplateInput): Promise<ApplyTemplateResponse> {
    return apiJson<ApplyTemplateResponse>(`/templates/${templateId}/apply`, {
      method: 'POST',
      json: input,
    });
  },
  // ── 0079: cross-org publication ──────────────────────────────────────
  // Reconcile the FULL publication set in one call. Mirrors skillsApi.setPublications().
  setPublications(
    templateId: string,
    pub: TemplatePublications,
  ): Promise<TemplatePublications> {
    return apiJson<TemplatePublicationsResponse>(`/templates/${templateId}/publications`, {
      method: 'PUT',
      json: { orgIds: pub.orgIds },
    }).then((r) => r.publications);
  },
  // Toggle ONE matrix cell ON. orgId=null = the all-organizations sentinel.
  // Tick ONE organization. 0168: `orgId: null` used to mean the sentinel and is
  // now a 400 — selectAll() is the route for that.
  addPublication(templateId: string, orgId: string): Promise<TemplatePublications> {
    return apiJson<TemplatePublicationsResponse>(`/templates/${templateId}/publications`, {
      method: 'POST',
      json: { orgId },
    }).then((r) => r.publications);
  },

  // THE SELECT-ALL. Puts a separate mark on every organization of the account;
  // any one of them can then be unticked on its own.
  selectAllOrgs(templateId: string): Promise<TemplatePublications> {
    return apiJson<TemplatePublicationsResponse>(`/templates/${templateId}/publications/all`, {
      method: 'POST',
    }).then((r) => r.publications);
  },

  // The same box, unticked. IT REMOVES ONLY THE MARKS IT MADE — hand-placed
  // marks survive, which is why the header box can end up half-selected.
  clearAllOrgs(templateId: string): Promise<TemplatePublications> {
    return apiJson<TemplatePublicationsResponse>(`/templates/${templateId}/publications/all`, {
      method: 'DELETE',
    }).then((r) => r.publications);
  },
  // Toggle ONE matrix cell OFF. orgId=null = remove the all-orgs sentinel.
  // Untick ONE organization. Works the same whether the mark was hand-placed or
  // put there by select-all — "after that, any one of them can be unticked" is
  // the whole point of 0168.
  removePublication(templateId: string, orgId: string): Promise<TemplatePublications> {
    return apiJson<TemplatePublicationsResponse>(
      `/templates/${templateId}/publications?orgId=${encodeURIComponent(orgId)}`,
      { method: 'DELETE' },
    ).then((r) => r.publications);
  },
  // Convenience: flip one cell to the desired state. Mirrors togglePublication().
  // Flip one matrix cell. `orgId === null` is the HEADER box (select-all), which
  // since 0168 is a real bulk write rather than a status flag.
  togglePublication(
    templateId: string,
    orgId: string | null,
    on: boolean,
  ): Promise<TemplatePublications> {
    if (orgId === null) {
      return on ? this.selectAllOrgs(templateId) : this.clearAllOrgs(templateId);
    }
    return on ? this.addPublication(templateId, orgId) : this.removePublication(templateId, orgId);
  },
};

export const templatesKeys = {
  all: ['templates'] as const,
  list: (params: ListTemplatesParams = {}) => [...templatesKeys.all, 'list', params] as const,
  detail: (templateId: string) => [...templatesKeys.all, 'detail', templateId] as const,
  // 0079: account-wide publication matrix (GET /templates/all).
  matrix: () => [...templatesKeys.all, 'matrix'] as const,
};
